/**
 * VB Server - Search API
 *
 * Full-text search for messages with highlighting.
 *
 * @example
 * import { searchMessages } from '@/api/server/search';
 *
 * const results = await searchMessages({
 *   orgId: 'org-123',
 *   query: 'emergency',
 *   channelIds: ['ch-1', 'ch-2'],
 * });
 */

import { serverGetFull } from './client';

// =============================================================================
// Types
// =============================================================================

/**
 * Search options
 */
export interface SearchOptions {
  orgId: string;
  query: string;
  channelIds?: string[];
  sourceTypes?: string[];
  deviceIds?: string[];
  feedIds?: string[];
  // Broadcastify Calls metadata filters
  talkgroups?: string[];
  talkgroupTags?: string[];
  radioIds?: string[];
  emergencyOnly?: boolean;
  tags?: string[];
  // Exclude filters
  excludeSourceTypes?: string[];
  excludeDeviceIds?: string[];
  excludeTalkgroupTags?: string[];
  excludeRadioIds?: string[];
  excludeTags?: string[];
  startTime?: string | number;
  endTime?: string | number;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  before?: string;
  after?: string;
}

/**
 * Search result message with highlighting
 */
export interface SearchResult {
  talk_id: string;
  org_id: string;
  channel_id: string;
  text: string;
  highlighted_text?: string;
  speaker_id?: string | null;
  tags?: string[];
  confidence?: number;
  duration?: number;
  device_id?: string | null;
  source_type?: string;
  feed_id?: string | null;
  // Broadcastify Calls metadata
  radio_id?: string | null;
  talkgroup?: string | null;
  talkgroup_tag?: string | null;
  radio_tag?: string | null;
  frequency?: number | null;
  system_id?: number | null;
  emergency?: boolean;
  encrypted?: boolean;
  created: string;
  score: number;
}

/**
 * Search response
 */
export interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  hasMore?: boolean;
}

/**
 * OpenSearch-compatible hit format (for backward compatibility)
 */
export interface SearchHit {
  _id: string;
  _score: number;
  _source: {
    talk_id: string;
    org_id: string;
    channel_id: string;
    text: string;
    speaker_id?: string | null;
    tags?: string[];
    confidence?: number;
    duration?: number;
    device_id?: string | null;
    source_type?: string;
    feed_id?: string | null;
    radio_id?: string | null;
    talkgroup?: string | null;
    talkgroup_tag?: string | null;
    radio_tag?: string | null;
    frequency?: number | null;
    system_id?: number | null;
    emergency?: boolean;
    encrypted?: boolean;
    created: string;
  };
  highlight?: {
    text?: string[];
  };
}

/**
 * OpenSearch-compatible response format (for backward compatibility)
 */
export interface OpenSearchResponse {
  hits: {
    total: { value: number };
    hits: SearchHit[];
  };
  hasMore?: boolean;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Search messages with full-text query
 *
 * @param options - Search options
 * @returns Search results
 */
export async function searchMessages(
  options: SearchOptions,
  signal?: AbortSignal
): Promise<SearchResponse> {
  const params = new URLSearchParams();
  params.set('org_id', options.orgId);
  params.set('q', options.query);

  if (options.channelIds?.length) {
    params.set('channel_ids', options.channelIds.join(','));
  }
  if (options.sourceTypes?.length) {
    params.set('source_types', options.sourceTypes.join(','));
  }
  if (options.deviceIds?.length) {
    params.set('device_ids', options.deviceIds.join(','));
  }
  if (options.feedIds?.length) {
    params.set('feed_ids', options.feedIds.join(','));
  }
  if (options.talkgroups?.length) {
    params.set('talkgroups', options.talkgroups.join(','));
  }
  if (options.talkgroupTags?.length) {
    params.set('talkgroup_tags', options.talkgroupTags.join(','));
  }
  if (options.radioIds?.length) {
    params.set('radio_ids', options.radioIds.join(','));
  }
  if (options.emergencyOnly) {
    params.set('emergency_only', 'true');
  }
  if (options.tags?.length) {
    params.set('tags', options.tags.join(','));
  }
  // Exclude filters
  if (options.excludeSourceTypes?.length) {
    params.set('exclude_source_types', options.excludeSourceTypes.join(','));
  }
  if (options.excludeDeviceIds?.length) {
    params.set('exclude_device_ids', options.excludeDeviceIds.join(','));
  }
  if (options.excludeTalkgroupTags?.length) {
    params.set('exclude_talkgroup_tags', options.excludeTalkgroupTags.join(','));
  }
  if (options.excludeRadioIds?.length) {
    params.set('exclude_radio_ids', options.excludeRadioIds.join(','));
  }
  if (options.excludeTags?.length) {
    params.set('exclude_tags', options.excludeTags.join(','));
  }
  if (options.startTime) {
    const start =
      typeof options.startTime === 'number'
        ? new Date(options.startTime).toISOString()
        : options.startTime;
    params.set('start', start);
  }
  if (options.endTime) {
    const end =
      typeof options.endTime === 'number'
        ? new Date(options.endTime).toISOString()
        : options.endTime;
    params.set('end', end);
  }
  if (options.order) {
    params.set('order', options.order);
  }
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (options.offset) {
    params.set('offset', String(options.offset));
  }
  if (options.before) {
    params.set('before', options.before);
  }
  if (options.after) {
    params.set('after', options.after);
  }

  const response = await serverGetFull<SearchResult[]>(
    `/messages/search?${params.toString()}`,
    signal
  );

  return {
    results: response.data || [],
    total: response.total || response.data?.length || 0,
    query: options.query,
    hasMore: response.hasMore
  };
}

/**
 * Search messages and return in OpenSearch-compatible format
 * For backward compatibility with existing dashboard code
 *
 * @param options - Search options
 * @returns OpenSearch-formatted response
 */
export async function searchMessagesOpenSearch(
  options: SearchOptions
): Promise<OpenSearchResponse> {
  const response = await searchMessages(options);

  // Convert to OpenSearch format
  const hits: SearchHit[] = response.results.map((result) => ({
    _id: result.talk_id,
    _score: result.score,
    _source: {
      talk_id: result.talk_id,
      org_id: result.org_id,
      channel_id: result.channel_id,
      text: result.text,
      speaker_id: result.speaker_id,
      tags: result.tags,
      confidence: result.confidence,
      duration: result.duration,
      device_id: result.device_id,
      source_type: result.source_type,
      feed_id: result.feed_id,
      radio_id: result.radio_id,
      talkgroup: result.talkgroup,
      talkgroup_tag: result.talkgroup_tag,
      radio_tag: result.radio_tag,
      frequency: result.frequency,
      system_id: result.system_id,
      emergency: result.emergency,
      encrypted: result.encrypted,
      created: result.created
    },
    highlight: result.highlighted_text ? { text: [result.highlighted_text] } : undefined
  }));

  return {
    hits: {
      total: { value: response.total },
      hits
    },
    hasMore: response.hasMore
  };
}

/**
 * Get message count matching a query (no results returned)
 *
 * @param options - Search options
 * @returns Total count
 */
export async function getSearchCount(options: SearchOptions): Promise<number> {
  const response = await searchMessages({ ...options, limit: 0 });
  return response.total;
}
